import type { AccountClient } from './client';
import { applyDocPatch, docPatch } from './documents';
import { runImport, readSnapshot, snapshotHasData, type Counts, type ImportOutcome } from './importer';
import { listChanges, listOf, resolveIntents } from './intents';
import { ACCOUNT_HINT_KEY, DEVICE_KEYS, fieldOfKey, isListField, type DeviceField, type DocKey } from './keys';
import { applyOutbox, enqueueIntents, enqueuePatch, flushOutbox, outboxIsEmpty, OutboxStore, type Display } from './outbox';
import { stringList } from './sanitize';
import { parseJson, safeSet, type KeyValueStorage } from './storage';
import type { AccountDocument, AccountState } from './types';

/**
 * One signed-in user's account data in this tab: loading it, moving the
 * device's plans into it, and keeping it in step with the server.
 *
 * Plain TypeScript rather than React, so the steps read in order and nothing
 * depends on render timing. `AccountDataProvider` owns one per signed-in user
 * and draws what `onChange` reports.
 *
 * Every network step runs one at a time, in order, so an import, a flush and a
 * refresh never interleave.
 */

export type Mode = 'device' | 'importing' | 'account' | 'error';

export interface Notice {
  message: string;
  at: number;
}

export interface SessionView {
  mode: Mode;
  /** False while the app should wait (spinner) or can't show the account (error). */
  ready: boolean;
  notice: Notice | null;
}

/** Counts and statuses only: never ids, titles or anything about the person. */
export interface SyncReport {
  step: 'load' | 'import' | 'sweep' | 'flush';
  outcome: 'done' | 'retry' | 'rejected' | 'unverified' | 'dropped';
  status?: number;
  missing?: number;
  unresolved?: number;
  count?: number;
}

export interface SessionDeps {
  workosUserId: string;
  client: AccountClient;
  /**
   * For sending the outbox. One attempt per request: the session's own backoff
   * retries it, and signing out needs a prompt answer rather than a client
   * working through its retries.
   */
  flushClient?: Pick<AccountClient, 'batchIntents' | 'putDocument'>;
  storage: KeyValueStorage;
  /** This browser last showed this user's account, so its own keys are leftovers, not the plan. */
  hinted: boolean;
  /** `adopt`: the hooks must read their values again. */
  onChange: (view: SessionView, adopt: boolean) => void;
  report: (report: SyncReport) => void;
  newId: () => string;
  locks?: Pick<LockManager, 'request'>;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export const NOTICES = {
  imported: 'Your schedule is now saved to your account.',
  kept: "We couldn't move your schedule to your account yet. It's still saved on this device, and we'll keep trying.",
  offline: "We couldn't reach your account. Your schedule is still saved on this device.",
} as const;

const LOAD_RETRY_MS = [5_000, 15_000, 30_000, 60_000];
const IMPORT_RETRY_MS = [10_000, 30_000, 60_000, 120_000, 300_000];
const FLUSH_RETRY_MS = [5_000, 15_000, 30_000, 60_000, 120_000];
const FLUSH_DEBOUNCE_MS = 250;
const SWEEP_DEBOUNCE_MS = 500;
const REFRESH_DEBOUNCE_MS = 800;

type TimerName = 'load' | 'import' | 'flush' | 'sweep' | 'refresh';

const sum = (counts: Counts | undefined) => (counts ? Object.values(counts).reduce((a, b) => a + b, 0) : undefined);

export class AccountSession {
  private view: SessionView;
  private display: Display | null = null;
  private docs: Record<DocKey, AccountDocument | null> | null = null;
  private readonly outbox: OutboxStore;
  private queue: Promise<void> = Promise.resolve();
  private readonly timers = new Map<TimerName, unknown>();
  private readonly failures = new Map<TimerName, number>();
  private readonly queued = new Set<TimerName>();
  private readonly shownNotices = new Set<string>();
  private disposed = false;
  private readonly deps: SessionDeps;

  constructor(deps: SessionDeps) {
    this.deps = deps;
    this.outbox = new OutboxStore(deps.storage, deps.workosUserId);
    const deviceData = snapshotHasData(readSnapshot(deps.storage));
    // A first visit with plans on the device shows them at once. Otherwise
    // the device's lists are empty or leftovers, and the account is the plan.
    this.view = { mode: 'device', ready: deviceData && !deps.hinted, notice: null };
  }

  get current(): SessionView {
    return this.view;
  }

  get userId(): string {
    return this.deps.workosUserId;
  }

  get outboxKey(): string {
    return this.outbox.key;
  }

  start(): void {
    this.deps.onChange(this.view, false);
    this.enqueue('load', () => this.load());
  }

  /** The error screen's Retry. */
  retry(): void {
    this.cancel('load');
    this.failures.delete('load');
    this.set({ mode: 'device', ready: false });
    this.enqueue('load', () => this.load());
  }

  /** `mcw:onboarding-complete`: read the account again and start the move. */
  onboardingComplete(): void {
    if (this.view.mode === 'account') return;
    this.cancel('import');
    this.failures.delete('import');
    this.enqueue('load', () => this.load());
  }

  /** Back online: whatever was waiting on a timer goes now. */
  online(): void {
    for (const name of [...this.timers.keys()]) this.fire(name);
  }

  /** `mcw:before-sign-out`: true once the server holds every change. */
  async flushNow(): Promise<boolean> {
    if (this.view.mode === 'account') {
      this.cancel('flush');
      await this.enqueue('flush', () => this.flush());
    }
    return outboxIsEmpty(this.outbox.read());
  }

  /** A `storage` event from another tab. */
  storageEvent(key: string | null, newValue: string | null): void {
    if (this.view.mode !== 'account') return;
    if (key === null || key === this.outbox.key) {
      // Another tab changed something: read the account again.
      this.later('refresh', REFRESH_DEBOUNCE_MS);
    } else if (fieldOfKey(key) && newValue !== null) {
      // A tab still on the device wrote its lists back. Move them in too.
      this.later('sweep', SWEEP_DEBOUNCE_MS);
    }
  }

  /** Resolves once every step queued so far has run. */
  settled(): Promise<void> {
    return this.queue;
  }

  dismissNotice(): void {
    if (this.view.notice) this.set({ notice: null });
  }

  dispose(): void {
    this.disposed = true;
    for (const handle of this.timers.values()) this.clearTimer(handle);
    this.timers.clear();
  }

  /**
   * The seven hooks' storage in account mode.
   *
   * Reads give the account's copy in the form the device keeps it, so each
   * hook's own `read()` checks it. A write is compared with what this same
   * storage last handed out or accepted — what that hook was showing — and
   * only the difference is sent. Make one per hook instance.
   */
  createStorage(): KeyValueStorage {
    const last = new Map<string, unknown>();
    const write = (key: string, next: unknown) => {
      const field = fieldOfKey(key);
      if (!field) return false;
      const prev = last.has(key) ? last.get(key) : this.valueOf(field);
      last.set(key, next);
      this.change(field, prev, next);
      return true;
    };
    const device = this.deps.storage;
    return {
      getItem: (key) => {
        const field = fieldOfKey(key);
        if (!field) return device.getItem(key);
        const value = this.valueOf(field);
        last.set(key, value);
        return value === null ? null : JSON.stringify(value);
      },
      setItem: (key, value) => {
        const next = parseJson(value);
        if (!fieldOfKey(key)) device.setItem(key, value);
        // Unreadable is never taken to mean "empty": that would clear a list.
        else if (next !== undefined) write(key, next);
      },
      removeItem: (key) => {
        if (!write(key, null)) device.removeItem(key);
      },
    };
  }

  // --- the steps -----------------------------------------------------------------

  private async load(): Promise<void> {
    let state: AccountState;
    try {
      state = await this.deps.client.getState();
    } catch (err) {
      if (this.disposed) return;
      this.deps.report({ step: 'load', outcome: 'retry', status: statusOf(err) });
      if (this.view.ready && this.view.mode !== 'account') {
        // The device's own plans are on screen: keep them, say so once.
        this.set({ mode: 'device', ready: true });
        this.notify('offline');
      } else if (this.view.mode !== 'account') {
        this.set({ mode: 'error', ready: false });
      }
      this.backoff('load', LOAD_RETRY_MS);
      return;
    }
    if (this.disposed) return;
    this.failures.delete('load');

    if (!state.onboardingComplete) {
      // Until the questions are done, the device keeps working as it always has.
      this.set({ mode: 'device', ready: true });
      return;
    }

    if (!snapshotHasData(readSnapshot(this.deps.storage))) {
      this.enterAccount(state);
    } else if (this.deps.hinted || this.view.mode === 'account') {
      // Leftovers on a browser already showing the account: show it, then move them in.
      this.enterAccount(state);
      this.later('sweep', 0);
    } else {
      await this.importDevice();
    }
  }

  private async importDevice(): Promise<void> {
    this.set({ mode: 'importing', ready: true });
    const outcome = await runImport(this.importDeps());
    if (this.disposed) return;

    switch (outcome.kind) {
      case 'empty':
        // Another tab moved them first. Read the account it moved them into.
        this.enqueue('load', () => this.load());
        return;
      case 'done':
        this.failures.delete('import');
        this.deps.report({ step: 'import', outcome: 'done', count: sum(outcome.counts), unresolved: outcome.receipt.unresolved.length });
        this.enterAccount(outcome.state);
        this.notify('imported', true);
        return;
      default:
        this.set({ mode: 'device', ready: true });
        this.notify('kept');
        this.reportFailure('import', outcome);
        if (outcome.kind === 'retry') this.backoff('import', IMPORT_RETRY_MS);
    }
  }

  /** Device keys written while the account was showing: moved in, then shown. */
  private async sweep(): Promise<void> {
    if (this.view.mode !== 'account') return;
    const outcome = await runImport(this.importDeps());
    if (this.disposed) return;
    if (outcome.kind === 'done') {
      this.failures.delete('sweep');
      this.deps.report({ step: 'sweep', outcome: 'done', count: sum(outcome.counts) });
      this.adopt(outcome.state);
    } else if (outcome.kind !== 'empty') {
      this.reportFailure('sweep', outcome);
      if (outcome.kind === 'retry') this.backoff('sweep', IMPORT_RETRY_MS);
    }
  }

  private async refresh(): Promise<void> {
    if (this.view.mode !== 'account') return;
    try {
      const state = await this.deps.client.getState();
      if (this.disposed) return;
      this.adopt(state);
    } catch {
      // The screen keeps what it has; the next change or event reads again.
    }
  }

  private async flush(): Promise<void> {
    if (this.view.mode !== 'account' || !this.docs) return;
    const docs = this.docs;
    const run = () => flushOutbox(this.outbox, this.deps.flushClient ?? this.deps.client, docs);
    const result = this.deps.locks
      ? await this.deps.locks.request(`mcw.acct.flush:${this.deps.workosUserId}`, run)
      : await run();
    if (this.disposed) return;

    if (result.dropped) this.deps.report({ step: 'flush', outcome: 'dropped', count: result.dropped });
    for (const refusal of result.refused) this.deps.report({ step: 'flush', outcome: 'rejected', status: refusal.status });
    if (result.retry) {
      this.deps.report({ step: 'flush', outcome: 'retry' });
      this.backoff('flush', FLUSH_RETRY_MS);
    } else {
      this.failures.delete('flush');
    }
  }

  // --- account mode ----------------------------------------------------------------

  private enterAccount(state: AccountState): void {
    this.docs = { ...state.documents };
    this.display = applyOutbox(state, this.outbox.read());
    safeSet(this.deps.storage, ACCOUNT_HINT_KEY, this.deps.workosUserId);
    this.set({ mode: 'account', ready: true }, true);
    if (!outboxIsEmpty(this.outbox.read())) this.later('flush', 0);
    // A hook write already on its way when the mode changed lands on the
    // device; look once the hooks have moved over.
    this.later('sweep', SWEEP_DEBOUNCE_MS);
  }

  /** A newer copy of the account, with this tab's queued changes on top. */
  private adopt(state: AccountState): void {
    this.docs = { ...state.documents };
    this.display = applyOutbox(state, this.outbox.read());
    this.set({}, true);
  }

  private valueOf(field: DeviceField): unknown {
    const display = this.display;
    if (!display) return null;
    if (isListField(field)) return listOf(display.intents, field);
    return display.docs[field];
  }

  /** One hook's write, as the difference from what it showed before. */
  private change(field: DeviceField, prev: unknown, next: unknown): void {
    const display = this.display;
    if (!display) return;

    if (isListField(field)) {
      const { intents, ops } = resolveIntents(display.intents, listChanges(field, stringList(prev), stringList(next)));
      display.intents = intents;
      if (ops.length === 0) return;
      this.outbox.update((outbox) => enqueueIntents(outbox, ops));
    } else {
      const patch = docPatch(field, prev, next);
      if (Object.keys(patch).length === 0) return;
      display.docs[field] = applyDocPatch(field, display.docs[field], patch);
      this.outbox.update((outbox) => enqueuePatch(outbox, field, patch));
    }
    // Queued even after sign-out: the entry waits for this user's next visit.
    if (!this.disposed) this.later('flush', FLUSH_DEBOUNCE_MS);
  }

  // --- plumbing --------------------------------------------------------------------

  private importDeps() {
    return {
      storage: this.deps.storage,
      client: this.deps.client,
      workosUserId: this.deps.workosUserId,
      newId: this.deps.newId,
    };
  }

  private set(patch: Partial<SessionView>, adopt = false): void {
    if (this.disposed) return;
    this.view = { ...this.view, ...patch };
    this.deps.onChange(this.view, adopt);
  }

  /** Quiet notices show once per load; `always` for the one that reports success. */
  private notify(kind: keyof typeof NOTICES, always = false): void {
    if (!always && this.shownNotices.has(kind)) return;
    this.shownNotices.add(kind);
    this.set({ notice: { message: NOTICES[kind], at: Date.now() } });
  }

  private reportFailure(step: 'import' | 'sweep', outcome: ImportOutcome): void {
    if (outcome.kind === 'retry') {
      this.deps.report({
        step,
        outcome: outcome.reason === 'unverified' ? 'unverified' : 'retry',
        status: outcome.status,
        missing: sum(outcome.missing),
      });
    } else if (outcome.kind === 'rejected') {
      this.deps.report({ step, outcome: 'rejected', status: outcome.status });
    }
  }

  private task(name: TimerName): () => Promise<void> {
    switch (name) {
      case 'load':
        return () => this.load();
      case 'import':
        return () => this.importDevice();
      case 'flush':
        return () => this.flush();
      case 'sweep':
        return () => this.sweep();
      case 'refresh':
        return () => this.refresh();
    }
  }

  /** Runs after everything already queued. A step already waiting to run isn't queued twice. */
  private enqueue(name: TimerName, task: () => Promise<void>): Promise<void> {
    if (this.queued.has(name)) return this.queue;
    this.queued.add(name);
    this.queue = this.queue
      .then(() => {
        this.queued.delete(name);
        return this.disposed ? undefined : task();
      })
      .catch((err: unknown) => {
        this.queued.delete(name);
        if (import.meta.env.DEV) console.error('[account]', name, err);
      });
    return this.queue;
  }

  private later(name: TimerName, ms: number): void {
    if (this.disposed) return;
    this.cancel(name);
    this.timers.set(name, this.setTimer(() => this.fire(name), ms));
  }

  private fire(name: TimerName): void {
    this.cancel(name);
    void this.enqueue(name, this.task(name));
  }

  private cancel(name: TimerName): void {
    const handle = this.timers.get(name);
    if (handle !== undefined) this.clearTimer(handle);
    this.timers.delete(name);
  }

  private backoff(name: TimerName, steps: number[]): void {
    const n = this.failures.get(name) ?? 0;
    this.failures.set(name, n + 1);
    this.later(name, steps[Math.min(n, steps.length - 1)]);
  }

  private setTimer(fn: () => void, ms: number): unknown {
    return this.deps.setTimer ? this.deps.setTimer(fn, ms) : setTimeout(fn, ms);
  }

  private clearTimer(handle: unknown): void {
    if (this.deps.clearTimer) this.deps.clearTimer(handle);
    else clearTimeout(handle as ReturnType<typeof setTimeout>);
  }
}

function statusOf(err: unknown): number {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : 0;
}

/** The device keys, for `storage` events. */
export const isDeviceKey = (key: string | null): boolean =>
  key !== null && (Object.values(DEVICE_KEYS) as string[]).includes(key);
